import { CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Dropdown, Select, SelectProps, Tag, Typography, Input, Tooltip } from 'antd';
import React, { useState, useMemo, useRef } from 'react';
import StringTooltipIcon from '@/assets/images/icons/string-tooltip-icon.react.svg?react';
import i18n from '@/i18n';
import { MatchMode, VERSION_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { SplitModal } from '@/presentation/components/split-modal';
import { useStores } from '@/presentation/store';
import { FIELD_ALL, FieldKey, FieldScope } from '@/proto/field';
import { ClearBehavior, KeyDataType } from '../../protocol';
import styles from './index.module.scss';

const { Text } = Typography;

export interface TagFilterCondition {
  /**
   * Map 类型下的 key（交互中的 [key]）
   */
  key?: string;
  operator: string;
  values: string[];
}

export interface TagFilterValue {
  conditions: TagFilterCondition[];
}

export type TagFilterFullOption = {
  key?: string;
  /**
   * Map 类型下的 key 显示文案（用于 UI 展示），不传则回退使用 key
   */
  keyLabel?: string;
  operations?: Array<{ label: React.ReactNode; value: string }>;
  valueOptions?: Array<{ label: React.ReactNode; value: string }>;
};

type TagFilterSelectOption = { label: React.ReactNode; value: string };

const getDefaultFullOption = (fullOptions: TagFilterFullOption[]): TagFilterFullOption | undefined => (
  fullOptions.find(opt => !opt?.key)
);

const getFullOptionByKey = (
  fullOptions: TagFilterFullOption[],
  defaultFullOption: TagFilterFullOption | undefined,
  key?: string,
): TagFilterFullOption | undefined => (
  fullOptions.find(opt => opt?.key === key) ?? defaultFullOption
);

const getOperatorOptionsByKey = (
  fullOptions: TagFilterFullOption[],
  defaultFullOption: TagFilterFullOption | undefined,
  operatorOptions: Array<{ label: React.ReactNode; value: string }>,
  key?: string,
) => {
  const opt = getFullOptionByKey(fullOptions, defaultFullOption, key);
  if (opt?.operations && opt.operations.length > 0) {
    return opt.operations;
  }
  return operatorOptions;
};

const getValueOptionsByKey = (
  fullOptions: TagFilterFullOption[],
  defaultFullOption: TagFilterFullOption | undefined,
  innerOptions: Array<{ label: React.ReactNode; value: string }>,
  key?: string,
) => {
  const opt = getFullOptionByKey(fullOptions, defaultFullOption, key);
  if (opt?.valueOptions && opt.valueOptions.length > 0) {
    return opt.valueOptions;
  }
  return innerOptions;
};

const getDefaultKey = (keySelectOptions: TagFilterSelectOption[]): string => (
  keySelectOptions[0]?.value ?? ''
);

const makeDefaultConditions = (
  isMap: boolean,
  defaultKey: string,
  getDefaultOperator: (key?: string) => string,
): TagFilterCondition[] => (
  isMap
    ? [{ key: defaultKey, operator: getDefaultOperator(defaultKey), values: [] }]
    : [{ operator: getDefaultOperator(), values: [] }]
);

const normalizeConditions = (
  conds: TagFilterCondition[] | undefined,
  isMap: boolean,
  defaultKey: string,
  getDefaultOperator: (key?: string) => string,
  defaultConditions: TagFilterCondition[],
): TagFilterCondition[] => {
  const base = (conds && conds.length > 0) ? [...conds] : defaultConditions;
  if (!isMap) {
    return base.map(c => ({
      operator: c.operator ?? getDefaultOperator(),
      values: c.values ?? [],
    }));
  }
  return base.map((c) => {
    const k = typeof c.key === 'string' ? c.key : defaultKey;
    return {
      key: k,
      operator: c.operator ?? getDefaultOperator(k),
      values: c.values ?? [],
    };
  });
};

const buildUsedOperators = (localConditions: TagFilterCondition[], isMap: boolean) => (
  new Set(localConditions.map(cond => (isMap ? `${cond.key ?? ''}::${cond.operator}` : cond.operator)))
);

const getAvailableOperatorOptions = (
  localConditions: TagFilterCondition[],
  isMap: boolean,
  usedOperators: Set<string>,
  getCandidates: (key?: string) => Array<{ label: React.ReactNode; value: string }>,
  currentIndex: number,
) => {
  const current = localConditions[currentIndex];
  const currentOperator = current?.operator;
  const currentKey = isMap ? (current?.key ?? '') : '';
  const candidates = getCandidates(isMap ? current?.key : undefined);
  return candidates.filter((opt) => {
    if (opt.value === currentOperator) return true;
    const usedKey = isMap ? `${currentKey}::${opt.value}` : opt.value;
    return !usedOperators.has(usedKey);
  });
};

const getFirstUnusedOperator = (
  isMap: boolean,
  usedOperators: Set<string>,
  getCandidates: (key?: string) => Array<{ label: React.ReactNode; value: string }>,
  key?: string,
) => {
  const candidates = getCandidates(key);
  const prefix = isMap ? `${key ?? ''}::` : '';
  for (const opt of candidates) {
    const usedKey = isMap ? `${prefix}${opt.value}` : opt.value;
    if (!usedOperators.has(usedKey)) return opt.value;
  }
  return candidates[0]?.value;
};

const getOperatorType = (operator: string): string => {
  if (NO_VALUE_OPERATORS.includes(operator as MatchMode)) return 'no-value';
  if (RANGE_VALUE_OPERATORS.includes(operator as MatchMode)) return 'range';
  if (SINGLE_VALUE_OPERATORS.includes(operator as MatchMode)) return 'single';
  return 'multi';
};

const isSimilarOperator = (oldOperator: string, newOperator: string): boolean => (
  getOperatorType(oldOperator) === getOperatorType(newOperator)
);

const filterConditionsForConfirm = (localConditions: TagFilterCondition[]) => (
  localConditions.filter((cond) => {
    if (NO_VALUE_OPERATORS.includes(cond.operator as MatchMode)) return true;
    return cond.values && cond.values.length > 0;
  })
);

const computeVisibilityAfterConfirm = (
  filteredConditionsLen: number,
  clearBehavior: ClearBehavior | undefined,
): boolean => {
  if (filteredConditionsLen > 0) return true;
  if (clearBehavior === ClearBehavior.ClearOnly) return true;
  return false;
};

const buildKeySelectOptions = (
  externalKeyOptions: TagFilterSelectOption[],
  fullOptions: TagFilterFullOption[],
): TagFilterSelectOption[] => {
  const map = new Map<string, TagFilterSelectOption>();

  externalKeyOptions.forEach((opt) => {
    if (opt?.value) {
      map.set(opt.value, opt);
    }
  });

  fullOptions.forEach((opt) => {
    if (opt?.key) {
      const value = opt.key;
      const nextLabel = opt.keyLabel ?? opt.key;
      const existing = map.get(value);
      // 外部 keyOptions 优先；但如果外部 label 缺失/等于 value，允许用 keyLabel 做更友好展示
      if (!existing?.label || existing.label === value) {
        map.set(value, { label: nextLabel, value });
      }
    }
  });

  return Array.from(map.values());
};

export interface TagFilterProps {
  value?: TagFilterValue;
  onChange?: (value: TagFilterValue) => void;
  /**
   * 点击 Tag 内的关闭按钮（X）时触发。
   * 语义上表示“移除该筛选字段”，与“清空条件但保留字段”区分开。
   */
  onRemove?: () => void;
  options?: Array<{ label: React.ReactNode; value: string }>;
  operatorOptions?: Array<{ label: React.ReactNode; value: string }>;
  /**
   * Map 类型下的 key 下拉选项（可选；也可由 fullOptions 推导）
   */
  keyOptions?: Array<{ label: React.ReactNode; value: string }>;
  /**
   * Map 类型下的“key + operator + value”组合配置
   * - fullOptions 可选
   * - 每项的 key/operations/valueOptions 也都可选
   * - operations 未传：回退使用 operatorOptions
   * - valueOptions 未传：回退使用 options（或远程加载得到的 options）
   */
  fullOptions?: TagFilterFullOption[];
  placeholder?: string;
  keyPlaceholder?: string;
  label?: string; // 字段标签，将显示在 tag 内部
  /**
   * 当点击确定或取消时，通知父组件字段的显示状态
   * @param visible - true 表示字段应该显示，false 表示字段应该隐藏
   */
  onVisibilityChange?: (visible: boolean) => void;
  content?: React.ReactNode;
  /**
   * 外部传入的 className，用于覆盖默认样式
   */
  className?: string;
  /**
   * 字段数据范围，用于请求远程下拉框的数据传参
   */
  fieldScope?: FieldScope | null; // string | number | null
  /**
   * 字段数据 Key，用于请求远程下拉框时指定 FieldKey
   * 如果未设置，则回退使用 filterName
   */
  fieldKey?: string;
  /**
   * 过滤字段名，用于后端查询（生成 filter 使用）
   */
  filterName?: string;
  /**
   * 决定交互与 filters 协议（默认 String）
   * - String/Number/Array: [operator] value
   * - Map: [key] [operator] value
   */
  keyDataType?: KeyDataType;

  showCloseButton?: boolean;
  /**
   * 禁用 TagFilter 内置 Dropdown（用于外部容器接管弹层展示）
   */
  disableDropdown?: boolean;
  /**
   * 当 disableDropdown=true 时，点击触发器回调（由外部切换面板内容）
   */
  onTriggerClick?: () => void;
  /**
   * 控制清空时的行为
   * - ClearOnly: 仅清空 conditions，不强制隐藏字段
   * - ClearAndHide: 清空 conditions 并建议隐藏字段
   */
  clearBehavior?: ClearBehavior;
  /**
   * 是否启用批量输入功能
   */
  batchEditMode?: boolean;
  /**
   * 批量输入提示文案
   */
  batchEditTips?: React.ReactNode;
  /**
   * 远程候选值加载后的转换函数（如单位换算、标签格式化等）
   */
  optionTransform?: (options: any[]) => any[];
  /**
   * 透传给值 Select 的额外属性（opt-in，用于定制搜索/下拉渲染等行为，如机型营销名称搜索）
   */
  valueSelectProps?: SelectProps;
  /**
   * 单选模式（opt-in，默认 false 保持原有多选 + 多条件行为）
   * 开启后：
   * - 值输入退化为单选（不再是多选 tags）
   * - 隐藏「添加」按钮，仅允许一条筛选条件
   */
  singleSelect?: boolean;
}

const DEFAULT_OPERATOR_OPTIONS = VERSION_MATCH_OPTIONS;

// 不需要值输入的操作符
const NO_VALUE_OPERATORS = [
  MatchMode.ISEMPTY,
  MatchMode.NOTEMPTY,
];

// 需要单值输入的操作符
const SINGLE_VALUE_OPERATORS: MatchMode[] = [];

// 需要范围输入的操作符
const RANGE_VALUE_OPERATORS = [
  MatchMode.Between,
];

interface ValueInputProps {
  operator: string;
  value: string[];
  onChange: (values: string[]) => void;
  options?: Array<{ label: React.ReactNode; value: string }>;
  placeholder?: string;
  loading?: boolean;
  /**
   * 透传给值 Select 的额外属性（opt-in，用于定制搜索/下拉渲染等行为）
   */
  valueSelectProps?: SelectProps;
}

/**
 * 空值输入组件 - 用于不需要输入值的操作符
 */
const EmptyValueInput: React.FC<ValueInputProps> = () => (
  <div className={styles.emptyInput}>
    <span className={styles.emptyText}>{i18n.t('无需输入值')}</span>
  </div>
);

/**
 * 单值输入组件 - 用于数值比较和字符串匹配
 */
const SingleValueInput: React.FC<ValueInputProps> = ({
  value, onChange, options, placeholder, loading, valueSelectProps,
}) => {
  if (options && options.length > 0) {
    // 有选项的选择器
    return (
      <Select
        value={value?.[0]}
        onChange={val => onChange([val])}
        style={{ flex: 1 }}
        placeholder={placeholder || i18n.t('请选择值')}
        options={options}
        showSearch
        loading={loading}
        {...valueSelectProps}
      />
    );
  }

  // 文本输入
  return (
    <Input
      value={value?.[0] || ''}
      onChange={e => onChange([e.target.value])}
      placeholder={placeholder || i18n.t('请输入值')}
      style={{ flex: 1 }}
    />
  );
};

/**
 * 多值输入组件 - 用于多选匹配
 */
const MultiValueInput: React.FC<ValueInputProps> = ({
  value, onChange, options = [], placeholder, loading, valueSelectProps,
}) => (
      <Select
      loading={loading}
        mode="tags"
        value={value}
        onChange={onChange}
        style={{ flex: 1 }}
        placeholder={placeholder || i18n.t('请选择')}
        options={options}
        maxTagCount="responsive"
        tagRender={(props: { label: React.ReactNode; closable?: boolean; onClose?: () => void }) => {
          const { label, closable, onClose } = props;
          return (
            <Tag
              closable={closable}
              onClose={onClose}
              style={{ marginRight: 3 }}
              className={styles.tag}
            >
              <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 150 }}>
                {label}
              </Text>
            </Tag>
          );
        }}
        {...valueSelectProps}
      />
);


/**
 * 范围输入组件 - 用于区间操作符
 */
const RangeValueInput: React.FC<ValueInputProps> = ({ value, onChange, options, placeholder }) => {
  const onMinValueChange = (minValue: string) => {
    onChange([minValue, value?.[1] || '']);
  };

  const onMaxValueChange = (maxValue: string) => {
    onChange([value?.[0] || '', maxValue]);
  };

  return (
    <div className={styles.rangeInput}>
      <Select
        showArrow={false}
        value={value?.[0]}
        onChange={onMinValueChange}
        options={options}
        placeholder={placeholder || i18n.t('最小值')}
        style={{ width: '45%' }}
      />
      <span className={styles.rangeSeparator}>~</span>
      <Select
        showArrow={false}
        value={value?.[1]}
        onChange={onMaxValueChange}
        options={options}
        placeholder={placeholder || i18n.t('最大值')}
        style={{ width: '45%' }}
      />
    </div>
  );
};

/**
 * 根据操作符类型选择对应的值输入组件
 */
const getValueInputComponent = (operator: string, singleSelect?: boolean): React.FC<ValueInputProps> => {
  if (NO_VALUE_OPERATORS.includes(operator as MatchMode)) {
    return EmptyValueInput;
  }

  if (RANGE_VALUE_OPERATORS.includes(operator as MatchMode)) {
    return RangeValueInput;
  }

  if (SINGLE_VALUE_OPERATORS.includes(operator as MatchMode)) {
    return SingleValueInput;
  }

  // 单选模式下退化为单值输入；否则默认使用多值输入
  return singleSelect ? SingleValueInput : MultiValueInput;
};

/**
 * 值输入组件包装器
 */
const ValueInput: React.FC<ValueInputProps & { singleSelect?: boolean }> = (props) => {
  const { singleSelect, ...rest } = props;
  const Component = getValueInputComponent(rest.operator, singleSelect);
  return <Component {...rest} />;
};

export interface TagFilterEditorProps extends Omit<TagFilterProps, 'content' | 'className' | 'showCloseButton'> {
  /**
   * 结束编辑（确定/取消）后回到上一级（由外部决定，比如切回列表）
   */
  onFinish?: () => void;
}

type SplitModalRef = {
  showModal: (value?: string) => void;
} | null;

/**
 * 可独立渲染的 TagFilter 面板内容（用于外部容器接管弹层展示）
 */
export const TagFilterEditor: React.FC<TagFilterEditorProps> = (props) => {
  const {
    value,
    onChange,
    options: externalOptions = [],
    operatorOptions = DEFAULT_OPERATOR_OPTIONS,
    keyOptions: externalKeyOptions = [],
    fullOptions = [],
    placeholder,
    keyPlaceholder,
    label,
    onVisibilityChange,
    fieldScope,
    fieldKey,
    filterName,
    onFinish,
    clearBehavior,
    keyDataType = KeyDataType.STRING,
    batchEditMode,
    batchEditTips,
    optionTransform,
    valueSelectProps,
    singleSelect,
  } = props;

  const { loadField } = useStores().FieldStore;
  const isMap = keyDataType === KeyDataType.MAP;

  const keySelectOptions = useMemo(
    () => buildKeySelectOptions(externalKeyOptions, fullOptions),
    [externalKeyOptions, fullOptions],
  );

  const defaultFullOption = useMemo(() => getDefaultFullOption(fullOptions), [fullOptions]);

  const [innerOptions, setInnerOptions] = useState(externalOptions);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getOperatorOptionsByKeyLocal = (key?: string) => getOperatorOptionsByKey(
    fullOptions,
    defaultFullOption,
    operatorOptions,
    key,
  );

  const getValueOptionsByKeyLocal = (key?: string) => getValueOptionsByKey(
    fullOptions,
    defaultFullOption,
    !fieldScope ? externalOptions : innerOptions,
    key,
  );

  const defaultKey = getDefaultKey(keySelectOptions);
  const getDefaultOperator = (key?: string) => getOperatorOptionsByKeyLocal(key)[0]?.value ?? '';

  const defaultConditions: TagFilterCondition[] = makeDefaultConditions(isMap, defaultKey, getDefaultOperator);

  const normalizeConditionsLocal = (conds?: TagFilterCondition[]): TagFilterCondition[] => normalizeConditions(
    conds,
    isMap,
    defaultKey,
    getDefaultOperator,
    defaultConditions,
  );

  const getInitialLocalConditions = () => normalizeConditionsLocal(value?.conditions);
  const [localConditions, setLocalConditions] = useState<TagFilterCondition[]>(getInitialLocalConditions);

  // value/operatorOptions 变化时，保持编辑态与外部值同步（比如切换不同字段）
  React.useEffect(() => {
    setLocalConditions(normalizeConditionsLocal(value?.conditions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, operatorOptions, isMap, defaultKey, JSON.stringify(fullOptions), JSON.stringify(externalKeyOptions)]);

  const usedOperators = useMemo(() => buildUsedOperators(localConditions, isMap), [localConditions, isMap]);

  const getAvailableOperatorOptionsLocal = (currentIndex: number) => getAvailableOperatorOptions(
    localConditions,
    isMap,
    usedOperators,
    getOperatorOptionsByKeyLocal,
    currentIndex,
  );

  const getFirstUnusedOperatorLocal = (key?: string) => getFirstUnusedOperator(
    isMap,
    usedOperators,
    getOperatorOptionsByKeyLocal,
    key,
  );

  const queryOptions = async () => {
    const key = fieldKey || filterName;
    if (loaded || !key || !fieldScope) return;
    setLoading(true);
    try {
      const field = await loadField(key as FieldKey, fieldScope as FieldScope);
      const fieldExceptAll = field?.filter(item => item.value !== FIELD_ALL && item.label !== '所有');
      setInnerOptions(optionTransform ? optionTransform(fieldExceptAll || []) : (fieldExceptAll || []));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TagFilterEditor] Failed to load field options:', error);
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!loaded) {
      void queryOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, fieldKey, filterName, fieldScope]);

  const handleAddCondition = () => {
    if (isMap) {
      const newOperator = getFirstUnusedOperatorLocal(defaultKey);
      setLocalConditions([...localConditions, { key: defaultKey, operator: newOperator, values: [] }]);
      return;
    }
    const newOperator = getFirstUnusedOperatorLocal();
    setLocalConditions([...localConditions, { operator: newOperator, values: [] }]);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions.length > 0 ? newConditions : []);
  };

  const handleKeyChange = (index: number, key: string) => {
    const newConditions = [...localConditions];
    const newOperator = getFirstUnusedOperatorLocal(key) ?? getDefaultOperator(key);
    newConditions[index] = {
      ...newConditions[index],
      key,
      operator: newOperator,
      values: [],
    };
    setLocalConditions(newConditions);
  };

  const handleKeyInputChange = (index: number, key: string) => {
    const newConditions = [...localConditions];
    newConditions[index] = {
      ...newConditions[index],
      key,
    };
    setLocalConditions(newConditions);
  };

  const handleOperatorChange = (index: number, operator: string) => {
    const newConditions = [...localConditions];
    const prev = newConditions[index];
    newConditions[index] = {
      ...prev,
      operator,
      values: isSimilarOperator(prev.operator, operator) ? prev.values : [],
    };
    setLocalConditions(newConditions);
  };

  const handleValuesChange = (index: number, values: string[]) => {
    const newConditions = [...localConditions];
    newConditions[index] = {
      ...newConditions[index],
      values,
    };
    setLocalConditions(newConditions);
  };

  const splitRefs = useRef<Map<number, SplitModalRef>>(new Map());

  const handleBatchEditConfirm = (index: number) => (values?: string[]) => {
    if (values && values.length > 0) {
      handleValuesChange(index, values);
    }
  };

  const handleShowBatchModal = (index: number) => {
    const ref = splitRefs.current.get(index);
    const currentValues = localConditions[index]?.values || [];
    ref?.showModal(currentValues.join('\n'));
  };

  const handleConfirm = () => {
    const filteredConditions = filterConditionsForConfirm(localConditions);
    onChange?.({ conditions: filteredConditions });
    if (onVisibilityChange) {
      onVisibilityChange(computeVisibilityAfterConfirm(filteredConditions.length, clearBehavior));
    }
    onFinish?.();
  };

  const handleCancel = () => {
    setLocalConditions(normalizeConditionsLocal(value?.conditions));
    const hasOriginalValue = !!(value?.conditions && value.conditions.length > 0);
    if (onVisibilityChange) {
      onVisibilityChange(hasOriginalValue);
    }
    onFinish?.();
  };


  return (
    <div className={styles.dropdownContent}>
      <div className={styles.conditionTitle}>
        <span>{label}</span>
      </div>
      <div className={styles.conditionContent}>
        {localConditions.map((condition, index) => (
          <div key={index} className={styles.conditionRow}>
            {isMap && (
              keySelectOptions.length > 0 ? (
                <Select
                  value={condition.key}
                  onChange={val => handleKeyChange(index, val)}
                  style={{ width: 120 }}
                  options={keySelectOptions}
                  placeholder={keyPlaceholder || i18n.t('请选择 key')}
                />
              ) : (
                <Input
                  value={condition.key}
                  onChange={e => handleKeyInputChange(index, e.target.value)}
                  placeholder={keyPlaceholder || i18n.t('请输入 key')}
                  style={{ width: 120 }}
                />
              )
            )}
            <Select
              value={condition.operator}
              onChange={val => handleOperatorChange(index, val)}
              style={{ width: 100 }}
              options={getAvailableOperatorOptionsLocal(index)}
            />
            <ValueInput
              operator={condition.operator}
              value={condition.values}
              onChange={values => handleValuesChange(index, values)}
              options={getValueOptionsByKeyLocal(isMap ? condition.key : undefined)}
              placeholder={placeholder}
              loading={loading}
              singleSelect={singleSelect}
              valueSelectProps={valueSelectProps}
            />
            <div className={styles.actionIcons}>
              {batchEditMode && (
                <SplitModal
                  ref={(ref) => {
                    if (ref) {
                      splitRefs.current.set(index, ref);
                    } else {
                      splitRefs.current.delete(index);
                    }
                  }}
                  callBack={handleBatchEditConfirm(index)}
                  onTrigger={() => handleShowBatchModal(index)}
                  trigger={
                    <Tooltip
                      title={
                        <div>
                          <div>{batchEditTips}</div>
                          <div onClick={() => handleShowBatchModal(index)} className={styles.tooltipLink}>
                            {i18n.t('点击icon进入批量输入编辑页')}
                          </div>
                        </div>
                      }
                    >
                      <span className={styles.batchEditIcon}>
                        <StringTooltipIcon />
                      </span>
                    </Tooltip>
                  }
                />
              )}
              <DeleteOutlined
                className={styles.deleteIcon}
                onClick={() => handleRemoveCondition(index)}
              />
            </div>
          </div>
        ))}
        {!singleSelect && (
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={handleAddCondition}
            style={{ padding: 0, marginTop: 6 }}
          >
            {i18n.t('添加')}
          </Button>
        )}
        <div className={styles.footer} style={{ marginTop: 8 }}>
          <Button onClick={handleCancel}>{i18n.t('取消')}</Button>
          <Button type="primary" onClick={handleConfirm}>{i18n.t('确定')}</Button>
        </div>
      </div>
    </div>
  );
};

export const TagFilter: React.FC<TagFilterProps> = (props) => {
  const {
    value,
    onChange,
    onRemove,
    options: externalOptions = [],
    operatorOptions = DEFAULT_OPERATOR_OPTIONS,
    keyOptions: externalKeyOptions = [],
    fullOptions = [],
    placeholder,
    keyPlaceholder,
    label,
    content,
    onVisibilityChange,
    className,
    fieldScope,
    fieldKey,
    filterName,
    showCloseButton = true,
    disableDropdown = false,
    onTriggerClick,
    clearBehavior,
    keyDataType = KeyDataType.STRING,
    batchEditMode,
    batchEditTips,
    optionTransform,
    singleSelect,
    valueSelectProps,
  } = props;

  const { loadField } = useStores().FieldStore;
  const isMap = keyDataType === KeyDataType.MAP;

  const keySelectOptions = useMemo(
    () => buildKeySelectOptions(externalKeyOptions, fullOptions),
    [externalKeyOptions, fullOptions],
  );

  const defaultFullOption = useMemo(() => getDefaultFullOption(fullOptions), [fullOptions]);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [innerOptions, setInnerOptions] = useState(externalOptions);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getOperatorOptionsByKeyLocal = (key?: string) => getOperatorOptionsByKey(
    fullOptions,
    defaultFullOption,
    operatorOptions,
    key,
  );

  const getValueOptionsByKeyLocal = (key?: string) => getValueOptionsByKey(
    fullOptions,
    defaultFullOption,
    !fieldScope ? externalOptions : innerOptions,
    key,
  );

  const defaultKey = getDefaultKey(keySelectOptions);
  // 获取默认操作符（对应 key 的 operatorOptions 的第一个）
  const getDefaultOperator = (key?: string) => getOperatorOptionsByKeyLocal(key)[0]?.value ?? '';

  const defaultConditions: TagFilterCondition[] = makeDefaultConditions(isMap, defaultKey, getDefaultOperator);

  const normalizeConditionsLocal = (conds?: TagFilterCondition[]): TagFilterCondition[] => normalizeConditions(
    conds,
    isMap,
    defaultKey,
    getDefaultOperator,
    defaultConditions,
  );

  const getInitialLocalConditions = () => normalizeConditionsLocal(value?.conditions);
  const [localConditions, setLocalConditions] = useState<TagFilterCondition[]>(getInitialLocalConditions);

  const conditionTexts = useMemo(() => {
    const texts: string[] = [];
    value?.conditions?.forEach((condition) => {
      const k = condition.key ?? '';
      const operatorCandidates = getOperatorOptionsByKeyLocal(isMap ? k : undefined);
      const operatorLabel = operatorCandidates.find(opt => opt.value === condition.operator)?.label
        || condition.operator;

      const valueCandidates = getValueOptionsByKeyLocal(isMap ? k : undefined);
      const valueLabels = (condition.values ?? []).map((val) => {
        const hit = valueCandidates.find(opt => opt.value === val);
        if (hit) return hit.label || val;
        if (val === '') return i18n.t('所有');
        return val;
      });

      if (isMap) {
        const keyLabel = keySelectOptions.find(opt => opt.value === k)?.label || k;
        texts.push(`${keyLabel} ${operatorLabel}${valueLabels.join(',')}`);
      } else {
        texts.push(`${operatorLabel}${valueLabels.join(',')}`);
      }
    });

    return texts.join(',');
  }, [value, innerOptions, operatorOptions, isMap, keySelectOptions, JSON.stringify(fullOptions)]);

  // 获取已使用的操作符（Map 模式下按 key 分组去重）
  const usedOperators = useMemo(() => buildUsedOperators(localConditions, isMap), [localConditions, isMap]);

  // 获取可用的操作符选项（过滤掉已使用的）
  const getAvailableOperatorOptionsLocal = (currentIndex: number) => getAvailableOperatorOptions(
    localConditions,
    isMap,
    usedOperators,
    getOperatorOptionsByKeyLocal,
    currentIndex,
  );

  // 获取第一个未使用的操作符
  const getFirstUnusedOperatorLocal = (key?: string) => getFirstUnusedOperator(
    isMap,
    usedOperators,
    getOperatorOptionsByKeyLocal,
    key,
  );

  const queryOptions = async () => {
    const key = fieldKey || filterName;
    if (loaded || !key || !fieldScope) {
      return;
    }
    setLoading(true);
    try {
      const field = await loadField(key as FieldKey, fieldScope as FieldScope);
      const fieldExceptAll = field?.filter(item => item.value !== FIELD_ALL && item.label !== '所有');
      setInnerOptions(optionTransform ? optionTransform(fieldExceptAll || []) : (fieldExceptAll || []));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[TagFilter] Failed to load field options:', error);
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  };

  const handleOpenDropdown = () => {
    setLocalConditions(normalizeConditionsLocal(value?.conditions));
    setDropdownVisible(true);
    if (!loaded) {
      void queryOptions();
    }
  };

  const handleVisibleChange = (visible: boolean) => {
    if (!visible) {
      // 关闭时恢复原始值（取消操作）
      setLocalConditions(normalizeConditionsLocal(value?.conditions));
    }
    setDropdownVisible(visible);
  };

  const handleConfirm = () => {
    // 确定时保存更改
    const filteredConditions = filterConditionsForConfirm(localConditions);

    onChange?.({
      conditions: filteredConditions,
    });
    // 根据 clearBehavior 控制是否隐藏字段
    if (onVisibilityChange) {
      onVisibilityChange(computeVisibilityAfterConfirm(filteredConditions.length, clearBehavior));
    }
    setDropdownVisible(false);
  };

  const handleCancel = () => {
    // 取消时恢复原始值
    setLocalConditions(normalizeConditionsLocal(value?.conditions));
    // 取消时，如果原来没有值，则隐藏字段
    const hasOriginalValue = !!(value?.conditions && value.conditions.length > 0);
    if (onVisibilityChange) {
      onVisibilityChange(hasOriginalValue);
    }
    setDropdownVisible(false);
  };

  const handleDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation(); // 阻止事件冒泡，避免触发打开弹窗
    // X 按钮语义：移除字段（由外部决定是否要做额外的“长期记忆”清理等）
    onRemove?.();
    onChange?.({
      conditions: [],
    });
    // 点击 X 时默认隐藏字段（清空条件并移除），不受 clearBehavior 影响
    onVisibilityChange?.(false);
  };

  const handleAddCondition = () => {
    if (isMap) {
      const newOperator = getFirstUnusedOperatorLocal(defaultKey);
      setLocalConditions([...localConditions, { key: defaultKey, operator: newOperator, values: [] }]);
      return;
    }
    const newOperator = getFirstUnusedOperatorLocal();
    setLocalConditions([...localConditions, { operator: newOperator, values: [] }]);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions.length > 0 ? newConditions : []);
  };

  const handleKeyChange = (index: number, key: string) => {
    const newConditions = [...localConditions];
    const newOperator = getFirstUnusedOperatorLocal(key) ?? getDefaultOperator(key);
    newConditions[index] = {
      ...newConditions[index],
      key,
      operator: newOperator,
      values: [],
    };
    setLocalConditions(newConditions);
  };

  const handleKeyInputChange = (index: number, key: string) => {
    const newConditions = [...localConditions];
    newConditions[index] = {
      ...newConditions[index],
      key,
    };
    setLocalConditions(newConditions);
  };

  const handleOperatorChange = (index: number, operator: string) => {
    const newConditions = [...localConditions];
    newConditions[index] = {
      ...newConditions[index],
      operator,
      // 切换操作符时重置值，除非是同类操作符
      values: isSimilarOperator(newConditions[index].operator, operator)
        ? newConditions[index].values
        : [],
    };
    setLocalConditions(newConditions);
  };

  const handleValuesChange = (index: number, values: string[]) => {
    const newConditions = [...localConditions];
    newConditions[index] = {
      ...newConditions[index],
      values,
    };
    setLocalConditions(newConditions);
  };

  const splitRefs = useRef<Map<number, SplitModalRef>>(new Map());

  const handleBatchEditConfirm = (index: number) => (values?: string[]) => {
    if (values && values.length > 0) {
      handleValuesChange(index, values);
    }
  };

  const handleShowBatchModal = (index: number) => {
    const ref = splitRefs.current.get(index);
    const currentValues = localConditions[index]?.values || [];
    ref?.showModal(currentValues.join('\n'));
  };

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      <div className={styles.conditionTitle}>{label}</div>
      <div className={styles.conditionContent}>
        {localConditions.map((condition, index) => (
          <div key={index} className={styles.conditionRow}>
            {isMap && (
              keySelectOptions.length > 0 ? (
                <Select
                  value={condition.key}
                  onChange={val => handleKeyChange(index, val)}
                  style={{ width: 120 }}
                  options={keySelectOptions}
                  placeholder={keyPlaceholder || i18n.t('请选择 key')}
                />
              ) : (
                <Input
                  value={condition.key}
                  onChange={e => handleKeyInputChange(index, e.target.value)}
                  placeholder={keyPlaceholder || i18n.t('请输入 key')}
                  style={{ width: 120 }}
                />
              )
            )}
            <Select
              value={condition.operator}
              onChange={val => handleOperatorChange(index, val)}
              style={{ width: 100 }}
              options={getAvailableOperatorOptionsLocal(index)}
            />
            <ValueInput
              operator={condition.operator}
              value={condition.values}
              onChange={values => handleValuesChange(index, values)}
              options={getValueOptionsByKeyLocal(isMap ? condition.key : undefined)}
              placeholder={placeholder}
              loading={loading}
              singleSelect={singleSelect}
              valueSelectProps={valueSelectProps}
            />
            <div className={styles.actionIcons}>
              {batchEditMode && (
                <SplitModal
                  ref={(ref) => {
                    if (ref) {
                      splitRefs.current.set(index, ref);
                    } else {
                      splitRefs.current.delete(index);
                    }
                  }}
                  callBack={handleBatchEditConfirm(index)}
                  onTrigger={() => handleShowBatchModal(index)}
                  trigger={
                    <Tooltip
                      title={
                        <div>
                          <div>{batchEditTips}</div>
                          <div onClick={() => handleShowBatchModal(index)} className={styles.tooltipLink}>
                            {i18n.t('点击icon进入批量输入编辑页')}
                          </div>
                        </div>
                      }
                    >
                      <span className={styles.batchEditIcon}>
                        <StringTooltipIcon />
                      </span>
                    </Tooltip>
                  }
                />
              )}
              <DeleteOutlined
                className={styles.deleteIcon}
                onClick={() => handleRemoveCondition(index)}
              />
            </div>
          </div>
        ))}
        {!singleSelect && (
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={handleAddCondition}
            style={{ padding: 0, marginTop: 6 }}
          >
            {i18n.t('添加')}
          </Button>
        )}
        <div className={styles.footer} style={{ marginTop: 8 }}>
          <Button onClick={handleCancel}>{i18n.t('取消')}</Button>
          <Button type="primary" onClick={handleConfirm}>{i18n.t('确定')}</Button>
        </div>
      </div>
    </div>
  );


  const triggerNode = (
    <div
      className={className || styles.tagFilter}
      onClick={disableDropdown ? onTriggerClick : handleOpenDropdown}
    >
      <Text
        className={styles.tagText}
        ellipsis={{ tooltip: `${label} ${conditionTexts || ''}` }}
      >
        {content ? content
          : <>
            <span className={styles.label}>{label}{conditionTexts ? '：' : ''}</span>
            <span className={styles.conditionText}>{conditionTexts}</span>
          </>}
      </Text>
      {
        showCloseButton && (
          <CloseOutlined
            className={styles.closeIcon}
            onClick={handleDelete}
          />
        )
      }
    </div>
  );

  if (disableDropdown) {
    return triggerNode;
  }

  return (
    <Dropdown
      overlay={dropdownContent}
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={handleVisibleChange}
      placement="bottomLeft"
    >
      {triggerNode}
    </Dropdown>
  );
};
